import React, { useState, useCallback, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Box } from '@chakra-ui/react';
import { useEmbeddedEffect } from '@machinemetrics/mm-react-embeddable';
import i18n from 'i18next';

import MMDrawer from 'components/MMDrawer';
import MMEmbeddableZoneWithLoader from 'components/MMEmbeddableZoneWithLoader';
import {
  DRAWER_HEADER_EVENT,
  DRAWER_CLOSE_EVENT,
  DRAWER_SUBMIT_EVENT,
} from 'lib/embedMessages';

/**
 * Host shell for embedded MFE drawers: MMDrawer + zone + loader + standard
 * lifecycle/header message listeners.
 */
const EmbeddedMMDrawer = ({
  embedZoneId,
  messageSource,
  src,
  path,
  context,
  isOpen,
  onDismiss,
  onSubmit,
  defaultTitle,
  zoneParams,
  sandbox,
  referrerPolicy,
  banner,
  hideHeader = false,
  ariaLabel,
  loaderTimeoutMs,
  renderLoaderTimeoutFallback,
}) => {
  const [title, setTitle] = useState(defaultTitle);
  const [closeAction, setCloseAction] = useState('dismiss');
  const [hideHostHeader, setHideHostHeader] = useState(false);
  const [drawerBackAt, setDrawerBackAt] = useState(0);
  const [zoneRemountKey, setZoneRemountKey] = useState(0);

  useEffect(() => {
    if (!isOpen) {
      setTitle(defaultTitle);
      setCloseAction('dismiss');
      setHideHostHeader(false);
      setDrawerBackAt(0);
      setZoneRemountKey(0);
    }
  }, [isOpen, defaultTitle]);

  // Remounting the zone (fresh key) restarts the whole load: iframe, loader
  // spinner and its never-ready timeout. Handed to the consumer's timeout
  // fallback as `retry`.
  const retryZone = useCallback(() => {
    setZoneRemountKey((key) => {
      return key + 1;
    });
  }, []);

  const renderTimeoutFallback = useMemo(() => {
    if (!renderLoaderTimeoutFallback) {
      return null;
    }
    return () => {
      return renderLoaderTimeoutFallback({ retry: retryZone });
    };
  }, [renderLoaderTimeoutFallback, retryZone]);

  useEmbeddedEffect(
    embedZoneId,
    `${messageSource}:${DRAWER_HEADER_EVENT}`,
    useCallback((payload) => {
      if (payload?.title != null) {
        setTitle(payload.title);
      }
      if (payload?.closeAction) {
        setCloseAction(payload.closeAction);
        if (payload.closeAction === 'dismiss') {
          setDrawerBackAt(0);
        }
      }
      if (payload?.hideHostHeader != null) {
        setHideHostHeader(Boolean(payload.hideHostHeader));
      }
    }, [])
  );

  useEmbeddedEffect(
    embedZoneId,
    `${messageSource}:${DRAWER_CLOSE_EVENT}`,
    useCallback(() => {
      onDismiss();
    }, [onDismiss])
  );

  useEmbeddedEffect(
    embedZoneId,
    `${messageSource}:${DRAWER_SUBMIT_EVENT}`,
    useCallback(
      (result) => {
        if (onSubmit) {
          onSubmit(result);
        }
      },
      [onSubmit]
    )
  );

  const handleDrawerClose = useCallback(() => {
    if (closeAction === 'back') {
      setDrawerBackAt(Date.now());
      return;
    }
    onDismiss();
  }, [closeAction, onDismiss]);

  const zoneContext = useMemo(() => {
    if (!context) {
      return null;
    }
    return { ...context, drawerBackAt };
  }, [context, drawerBackAt]);

  const resolvedZoneParams = useMemo(() => {
    return {
      language: i18n.language,
      ...zoneParams,
    };
  }, [zoneParams]);

  return (
    <MMDrawer
      isOpen={isOpen}
      onClose={handleDrawerClose}
      title={title}
      hideHeader={hideHostHeader || hideHeader}
      closeAction={closeAction}
      ariaLabel={ariaLabel}
    >
      {banner}
      <Box
        flex="1"
        minH={0}
        display="flex"
        flexDirection="column"
        overflow="hidden"
        h="100%"
      >
        {zoneContext && (
          <MMEmbeddableZoneWithLoader
            key={zoneRemountKey}
            id={embedZoneId}
            src={src}
            path={path}
            context={zoneContext}
            params={resolvedZoneParams}
            sandbox={sandbox}
            referrerPolicy={referrerPolicy}
            timeoutMs={loaderTimeoutMs}
            renderTimeoutFallback={renderTimeoutFallback}
          />
        )}
      </Box>
    </MMDrawer>
  );
};

EmbeddedMMDrawer.propTypes = {
  embedZoneId: PropTypes.string.isRequired,
  messageSource: PropTypes.string.isRequired,
  src: PropTypes.string.isRequired,
  path: PropTypes.string.isRequired,
  context: PropTypes.object,
  isOpen: PropTypes.bool.isRequired,
  onDismiss: PropTypes.func.isRequired,
  onSubmit: PropTypes.func,
  defaultTitle: PropTypes.string.isRequired,
  zoneParams: PropTypes.object,
  /** Optional iframe `sandbox` attribute, forwarded through the zone to the
   * iframe. When absent the iframe renders unsandboxed (APM default). */
  sandbox: PropTypes.string,
  /** Optional iframe `referrerPolicy` attribute, forwarded like `sandbox`. */
  referrerPolicy: PropTypes.string,
  /** Optional host-side banner rendered inside the drawer, immediately above
   * the embedded zone (e.g. JobDocsDrawer's contextual document header). */
  banner: PropTypes.node,
  /** Host-forced header hide, OR'd with the embed-driven hideHostHeader
   * message (e.g. JobDocsDrawer hides the drawer chrome while a document is
   * open so the contextual header becomes the top bar). */
  hideHeader: PropTypes.bool,
  /** Accessible name for the drawer dialog — essential when the header (and
   * with it the visible title) is hidden. Optional; APM consumers keep the
   * titled header and are unaffected. */
  ariaLabel: PropTypes.string,
  /** How long the loader waits for the embed's 'ready' before showing the
   * timeout fallback. No timeout when absent (APM default). */
  loaderTimeoutMs: PropTypes.number,
  /** Render prop for the loader's timeout fallback UI. Called with
   * `{ retry }` — invoking `retry` remounts the zone for a fresh load
   * attempt. No fallback when absent (APM default: spinner forever). */
  renderLoaderTimeoutFallback: PropTypes.func,
};

export default EmbeddedMMDrawer;
